import React from "react";
import { Scale, ShieldCheck, Ruler, Component, FileText, Zap, Briefcase, Landmark } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  title: "Airport Transfers",
  description: "Direct, punctual luxury transport to all major private and commercial aviation hubs with real-time flight tracking.",
  icon: Zap,
  tag: "AVIATION"
}, {
  title: "Executive Chauffeur",
  description: "Professional chauffeurs providing discreet, door-to-door transit for the most discerning global clients.",
  icon: Briefcase,
  tag: "EXECUTIVE"
}, {
  title: "Hourly Hire",
  description: "Flexible, dedicated chauffeur service available for your schedule's specific requirements and precise duration.",
  icon: Ruler,
  tag: "FLEXIBLE"
}, {
  title: "Corporate Accounts",
  description: "Strategic mobility solutions with streamlined billing and comprehensive reporting for high-volume enterprise needs.",
  icon: Landmark,
  tag: "CORPORATE"
}, {
  title: "Event Services",
  description: "Precision-managed fleet coordination for galas, corporate summits, and high-profile private occasions.",
  icon: Component,
  tag: "EVENTS"
}, {
  title: "Diplomatic Services",
  description: "Highest-tier security and confidentiality protocols for government, embassy, and official delegations.",
  icon: ShieldCheck,
  tag: "SECURE"
}];
export default function OurServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="14865" id="our-services" ref={ref} className="relative w-full py-24 overflow-hidden bg-background text-foreground">
      {/* Visual background elements */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className={cn("mb-16 max-w-2xl transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl font-default font-semibold tracking-tight mb-6 text-foreground">
            Bespoke Mobility Solutions
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Experience the pinnacle of professional transportation. We combine meticulous planning 
            and premium fleet management to define the standards of executive travel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => <div key={i} data-index={i} className={cn("transition-all duration-700 delay-[var(--delay)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} style={{
          "--delay": `${i * 100}ms`
        } as React.CSSProperties}>
              <Card className="group h-full bg-card rounded-lg border-t border-t-ring/20 border-x-transparent border-b-transparent transition-all duration-300 hover:scale-[1.02] hover:border-t-ring hover:shadow-[0_-8px_30px_-10px_var(--color-ring)]/40 flex flex-col justify-between">
                <CardHeader className="pb-4">
                  <div className="mb-6 inline-flex items-center justify-center size-12 rounded-lg bg-accent/10 border border-ring/10 group-hover:border-ring/50 transition-colors duration-300">
                    <service.icon className="size-6 text-ring stroke-[1.5]" aria-hidden="true" />
                  </div>
                  <CardTitle className="font-default text-2xl font-semibold text-foreground group-hover:text-ring transition-colors duration-300">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-muted-foreground font-sans text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-4 border-t border-border/5 mt-auto">
                  <Badge variant="outline" className="font-mono text-[10px] tracking-widest text-muted-foreground/60 border-ring/20 group-hover:text-ring group-hover:border-ring/40 transition-colors duration-300 uppercase">
                    {service.tag}
                  </Badge>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}